/**
 * §7.32 Workforce Capacity Overlay.
 *
 * The companion surface to §7.6 Healthcare Capacity. §7.6 answers "do we
 * have enough BEDS?" — but in COVID-2020 the binding constraint in many
 * regions was nurses / doctors / respiratory therapists, not beds. HCW
 * absenteeism (sick at work) is the compounding factor: infected HCWs
 * become both unable-to-work AND further nosocomial-transmission sources.
 *
 * Model (kept simple + transparent):
 *   1. Project daily incidence via simulateSeird on the chosen pathogen × region.
 *   2. HCW infection rate = general-pop incidence × hcwMultiplier (default
 *      3.5×). Source priors below.
 *   3. Each infected HCW is unavailable for `isolationDays` (default 10;
 *      CDC/UKHSA isolation guidance for SARS-CoV-2 surge era).
 *   4. availableHCW(t) = totalHCW − Σ infections-in-last-isolationDays
 *   5. Hospital census from a deterministic LOS convolution (mean 7d), then
 *      nurse demand = ICU patients × 1/icuRatio + ward patients × 1/wardRatio.
 *   6. Safety breach = day-N when nurses-needed > nurses-available.
 *
 * Mitigations (operator-tunable):
 *   - Retiree mobilisation pool (one-time supply bump after activation lag)
 *   - Surge-licensure waivers (Y/N; enables out-of-state + retiree practice)
 *   - Nursing-student deployment (Y/N; +10% supply at limited-license scope)
 *
 * Source priors (cited; do not invent):
 *   - OECD Health at a Glance 2023: USA ~12 nurses + 3 doctors / 1000 pop;
 *     UK ~8+3; Germany ~14+4.5; France ~11+3.4; India ~1.7+0.9; Australia
 *     ~13+4. Already encoded in plugin-region-database
 *     `healthcareCapacity.hcwsPer1000` (combined nurse+doctor+aux). We split
 *     using a 0.70 nurse fraction — OECD HRH composition typical value.
 *   - HCW infection multiplier (3.5× default): Chou et al. "Epidemiology of
 *     and Risk Factors for Coronavirus Infection in Health Care Workers"
 *     (Ann Intern Med 2020; doi:10.7326/M20-1632); Lombardy retrospective
 *     (Lancet Respir Med 2020; doi:10.1016/S2213-2600(20)30200-1) — both
 *     reported 3-5× general-pop SARS-CoV-2 incidence among HCWs at surge
 *     peak. We default to 3.5× and let operators slide 1.5×-5×.
 *   - Safety ratios (ANA / RCN / Joint Commission):
 *       ICU normal:    1 nurse : 1-2 patients   (we use 1:2)
 *       Ward normal:   1 nurse : 4-6 patients   (we use 1:4)
 *       Crisis-SoC:    1 nurse : 6-8 ICU pts    (use 1:6 as "unsafe-but-accepted")
 *   - Isolation duration 5-14d (CDC + UKHSA SARS-CoV-2 guidance window).
 *
 * Limitations (tracked):
 *   - Combined HCW number — region database does not yet split nurse vs
 *     doctor vs RT. Nurse fraction assumed 0.70 globally. Future:
 *     plugin-region-database HRH composition split.
 *   - LOS convolution is a simple gamma-ish approximation (mean 7d ward,
 *     +3d ICU) rather than the full projectHospitalCensus call — keeps
 *     the surface self-contained and the math visible.
 *   - Nosocomial-transmission compounding is NOT yet modelled (an
 *     infected HCW further infecting colleagues). Conservative — actual
 *     workforce loss can be worse than this surface shows.
 *   - Retiree-pool activation lag fixed at 14d (credentialing + N95-fit-
 *     test cycle from NY 2020 retrospective).
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

// OECD HRH composition convention: nurses are ~70% of clinical HCW workforce
// (the rest = doctors + aux + RTs). Used to split the combined hcwsPer1000
// into nurse + non-nurse. Source: OECD Health Statistics 2023, "Health
// workforce: nurses + doctors per 1000".
const NURSE_FRACTION = 0.70;

// Safety ratios — patients per nurse. Above these = unsafe staffing.
// Crisis-Standards-of-Care = upper limit beyond which care quality is
// known-degraded (ANA / RCN). Source notes in surface docblock.
const RATIO_ICU_NORMAL = 2;        // 1 nurse per 2 ICU patients
const RATIO_ICU_CRISIS = 6;        // crisis-standards-of-care
const RATIO_WARD_NORMAL = 4;       // 1 nurse per 4 general-ward patients
const RATIO_WARD_CRISIS = 8;       // crisis-standards-of-care

// Retiree-mobilisation activation lag (NY 2020 retrospective — credentialing
// + N95-fit-test + facility onboarding cycle).
const RETIREE_ACTIVATION_LAG_DAYS = 14;

// Nursing-student supply uplift (limited-license scope, supervised).
// Conservative — Italian medical-student deployment 2020 retrospective.
const STUDENT_SUPPLY_UPLIFT = 0.10;

// Surge-licensure waiver supply uplift (out-of-state nurse mobility + retiree
// practice authority). Conservative.
const LICENSURE_WAIVER_SUPPLY_UPLIFT = 0.05;

interface WorkforceView {
  pathogen: NonNullable<ReturnType<typeof findPathogen>>;
  region: NonNullable<ReturnType<typeof findRegion>>;
  dailyIncidence: number[];
  hospitalCensus: number[];
  icuCensus: number[];
  nursesNeededDaily: number[];
  nursesAvailableDaily: number[];
  baselineNurses: number;
  baselineDoctors: number;
  safetyBreachDay: number | null;
  crisisBreachDay: number | null;
  peakAbsenteeismPct: number;
  peakAbsenteeismDay: number;
}

export function WorkforceCapacity() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const { pathogenId, regionId, setPathogenId, setRegionId } = useScenario();

  const [hcwMultiplier, setHcwMultiplier] = useState<number>(3.5);
  const [isolationDays, setIsolationDays] = useState<number>(10);
  const [retireePoolSize, setRetireePoolSize] = useState<number>(0);
  const [licensureWaivers, setLicensureWaivers] = useState<boolean>(false);
  const [studentsDeployed, setStudentsDeployed] = useState<boolean>(false);

  const view: WorkforceView | null = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;

    const durationDays = 180;
    const epi = simulateSeird({
      N: region.population.total, I0: 500,
      beta, gamma, sigma, ifr,
      durationDays, dtDays: 1,
    });

    const S = epi.compartments.S!;
    const dailyIncidence: number[] = [];
    for (let i = 1; i < S.length; i++) {
      dailyIncidence.push(Math.max(0, S[i - 1]! - S[i]!));
    }

    // Hospital census via a transparent exponential-decay convolution.
    // Each day's admissions = incidence × hospitalisation rate; each patient
    // discharges at rate 1/LOS. This is the deterministic "1-compartment LOS"
    // approximation — adequate for showing absenteeism dynamics, not as
    // precise as projectHospitalCensus's gamma convolution but the math is
    // visible on the surface.
    const ihr = pathogen.icuFraction?.value !== undefined
      ? Math.max(0.02, Math.min(0.20, pathogen.icuFraction.value * 0.5))
      : 0.05;
    const icuFractionOfHosp = pathogen.icuFraction?.value ?? 0.15;
    const losWard = pathogen.hospLengthOfStayDays?.mean ?? 7;
    const losIcu = losWard + 3;

    const hospitalCensus: number[] = new Array(dailyIncidence.length).fill(0);
    const icuCensus: number[] = new Array(dailyIncidence.length).fill(0);
    let hosp = 0;
    let icu = 0;
    for (let t = 0; t < dailyIncidence.length; t++) {
      const admits = dailyIncidence[t]! * ihr;
      const icuAdmits = admits * icuFractionOfHosp;
      const wardAdmits = admits - icuAdmits;
      hosp += wardAdmits - hosp / losWard;
      icu  += icuAdmits  - icu  / losIcu;
      if (hosp < 0) hosp = 0;
      if (icu < 0) icu = 0;
      hospitalCensus[t] = hosp;
      icuCensus[t] = icu;
    }

    // HCW supply model.
    const hcwsPer1000 = region.healthcareCapacity?.hcwsPer1000 ?? 5;
    const totalHCW = hcwsPer1000 * (region.population.total / 1000);
    const baselineNurses = totalHCW * NURSE_FRACTION;
    const baselineDoctors = totalHCW * (1 - NURSE_FRACTION);

    // HCW daily-infection projection. The general-pop daily attack rate
    // (dailyIncidence/N) is multiplied by hcwMultiplier to get the HCW
    // daily attack rate, then applied to the total HCW headcount.
    const hcwDailyInfections: number[] = dailyIncidence.map(inc => {
      const attackRate = inc / region.population.total;
      return Math.min(totalHCW, totalHCW * attackRate * hcwMultiplier);
    });

    // Rolling window: HCW unavailable on day t = sum of new HCW infections
    // in the prior `isolationDays`. Bound by total HCW.
    const hcwUnavailable: number[] = new Array(hcwDailyInfections.length).fill(0);
    for (let t = 0; t < hcwDailyInfections.length; t++) {
      const lo = Math.max(0, t - isolationDays + 1);
      let sum = 0;
      for (let j = lo; j <= t; j++) sum += hcwDailyInfections[j]!;
      hcwUnavailable[t] = Math.min(totalHCW, sum);
    }

    // Mitigation: retiree pool added after activation lag.
    const retireeBump: number[] = hcwUnavailable.map((_, t) =>
      t >= RETIREE_ACTIVATION_LAG_DAYS ? retireePoolSize : 0
    );
    // Licensure waivers + students apply as multiplicative supply uplifts on
    // the BASELINE workforce (they expand the eligible pool, they don't
    // un-infect anyone).
    const supplyUplift =
      (licensureWaivers ? LICENSURE_WAIVER_SUPPLY_UPLIFT : 0) +
      (studentsDeployed ? STUDENT_SUPPLY_UPLIFT : 0);

    const nursesAvailableDaily: number[] = hcwUnavailable.map((unavail, t) => {
      const baseline = baselineNurses * (1 + supplyUplift);
      // Allocate retiree pool by nurse fraction; assume retiree mix matches
      // workforce composition.
      const retireeNurses = retireeBump[t]! * NURSE_FRACTION;
      const lost = unavail * NURSE_FRACTION;
      return Math.max(0, baseline + retireeNurses - lost);
    });

    // Demand: nurses-needed to staff the hospital census at NORMAL ratios.
    const nursesNeededDaily: number[] = hospitalCensus.map((wardCensus, t) => {
      const icuPts = icuCensus[t]!;
      return wardCensus / RATIO_WARD_NORMAL + icuPts / RATIO_ICU_NORMAL;
    });

    // Breach days.
    let safetyBreachDay: number | null = null;
    let crisisBreachDay: number | null = null;
    for (let t = 0; t < nursesNeededDaily.length; t++) {
      const needed = nursesNeededDaily[t]!;
      const have = nursesAvailableDaily[t]!;
      if (safetyBreachDay === null && needed > have) {
        safetyBreachDay = t;
      }
      // Crisis-SoC: even relaxing to crisis ratios doesn't free up enough nurses.
      const crisisNeeded =
        hospitalCensus[t]! / RATIO_WARD_CRISIS + icuCensus[t]! / RATIO_ICU_CRISIS;
      if (crisisBreachDay === null && crisisNeeded > have) {
        crisisBreachDay = t;
      }
    }

    const absenteeismPctSeries = hcwUnavailable.map(u => totalHCW > 0 ? u / totalHCW : 0);
    const peakAbsenteeismPct = Math.max(...absenteeismPctSeries);
    const peakAbsenteeismDay = absenteeismPctSeries.indexOf(peakAbsenteeismPct);

    return {
      pathogen, region, dailyIncidence, hospitalCensus, icuCensus,
      nursesNeededDaily, nursesAvailableDaily,
      baselineNurses, baselineDoctors,
      safetyBreachDay, crisisBreachDay,
      peakAbsenteeismPct, peakAbsenteeismDay,
    };
  }, [pathogenId, regionId, hcwMultiplier, isolationDays, retireePoolSize, licensureWaivers, studentsDeployed]);

  if (!view) return <div>Loading…</div>;

  return (
    <SurfaceScaffold
      section="§7.32"
      title="Workforce Capacity"
      subtitle={<>
        HCW absenteeism model · nurse:patient safety-ratio breach · OECD priors
        · {view.region.names.english} · {(view.baselineNurses / 1e3).toFixed(0)}k baseline nurses
      </>}
      honestyRail={<>
        <HonestyRail variant="limitation">
          Region database carries combined HCW number; nurse split assumed 0.70 (OECD HRH convention).
          {' '}LOS convolution is the deterministic 1-compartment approximation — exposes the math but
          is coarser than §7.6's gamma form. OECD priors are a reference snapshot, not a live HRH feed.
        </HonestyRail>
        <HonestyRail variant="synthetic">
          HCW multiplier default 3.5× (Chou 2020 + Lombardy retrospective); operator-tunable — a
          modelled absenteeism association, not an observed feed. Nosocomial-transmission compounding
          (infected HCW → infected HCW) not modelled — actual loss may be worse. Pair the breach-day
          projection with an ensemble before acting.
        </HonestyRail>
      </>}
      kpis={<div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Day to unsafe staffing ratio</div>
          <div className={`kpi__value ${view.safetyBreachDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {view.safetyBreachDay !== null ? `Day ${view.safetyBreachDay}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">nurse:pt &gt; 1:{RATIO_ICU_NORMAL} ICU / 1:{RATIO_WARD_NORMAL} ward</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Day to crisis-SoC breach</div>
          <div className={`kpi__value ${view.crisisBreachDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {view.crisisBreachDay !== null ? `Day ${view.crisisBreachDay}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">even at 1:{RATIO_ICU_CRISIS} ICU / 1:{RATIO_WARD_CRISIS} ward</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak HCW absenteeism</div>
          <div className="kpi__value kpi__value--signal">{(view.peakAbsenteeismPct * 100).toFixed(1)}%</div>
          <div className="kpi__sub">day {view.peakAbsenteeismDay} · {Math.round(view.peakAbsenteeismPct * (view.baselineNurses + view.baselineDoctors)).toLocaleString()} HCWs</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Baseline workforce</div>
          <div className="kpi__value">{Math.round((view.baselineNurses + view.baselineDoctors) / 1e3).toLocaleString()}k</div>
          <div className="kpi__sub">{Math.round(view.baselineNurses / 1e3).toLocaleString()}k nurses + {Math.round(view.baselineDoctors / 1e3).toLocaleString()}k doctors</div>
        </div>
      </div>}
      inputs={<>
      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">HCW infection multiplier · ×{hcwMultiplier.toFixed(1)}</span>
            <input type="range" min="1.5" max="5" step="0.1" value={hcwMultiplier}
              onChange={e => setHcwMultiplier(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Isolation duration · {isolationDays}d</span>
            <input type="range" min="5" max="14" step="1" value={isolationDays}
              onChange={e => setIsolationDays(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Mitigations</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Retiree mobilisation pool · {retireePoolSize.toLocaleString()} HCWs</span>
            <input type="range" min="0" max={Math.max(1000, Math.round(view.baselineNurses * 0.3 / 1000) * 1000)} step="1000"
              value={retireePoolSize}
              onChange={e => setRetireePoolSize(parseInt(e.target.value, 10))} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--bm-text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={licensureWaivers}
              onChange={e => setLicensureWaivers(e.target.checked)} />
            Surge-licensure waivers (+5% supply)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--bm-text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={studentsDeployed}
              onChange={e => setStudentsDeployed(e.target.checked)} />
            Nursing-student deployment (+10% supply)
          </label>
        </div>
      </div>
      </>}
    >

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Nurses available vs nurses needed</span>
          <span className="panel__meta">red gap = staffing crisis</span>
        </div>
        <Sparkline values={view.nursesAvailableDaily} label="Nurses available (post-absenteeism + mitigations)" stroke="var(--bm-accent)" />
        <Sparkline values={view.nursesNeededDaily} label="Nurses needed (normal safety ratios)" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Hospital + ICU census driving demand</span>
        </div>
        <Sparkline values={view.hospitalCensus} label="General-ward census" stroke="var(--bm-signal)" />
        <Sparkline values={view.icuCensus} label="ICU census" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Recommended surge actions</span>
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--bm-text)', font: '13px var(--bm-font-mono)' }}>
          <li>Activate retiree-licence pool (lag ~{RETIREE_ACTIVATION_LAG_DAYS}d for credentialing + N95-fit-test)</li>
          <li>Issue surge-licensure waivers (out-of-state nurse mobility + retiree-practice authority)</li>
          <li>Deploy senior nursing-students at limited-license scope (supervised)</li>
          <li>International surge contracts (Cuban medical brigades / EU Civil Protection precedent)</li>
          <li>Re-deploy non-acute staff (elective-surgery teams) — paired with elective-care deferral</li>
        </ul>
      </div>
    </SurfaceScaffold>
  );
}
